import { CHANNEL_KIND } from '../catalog/channel';
import type { ChannelRef } from '../catalog/channel';
import { RAIL_ADDRESS_RANGE } from '../catalog/limits';
import { RAIL_TYPE_SPECS } from '../catalog/railType';
import type { RailTypeKey } from '../catalog/railType';
import { DALI_BRIDGE_ADDRESS } from '../catalog/dali';
import type { DaliBridgeId, LoadId, RailId, RoomId } from '../ids';
import type { DaliBridge, DinRail, Load, RcuConfig, Room, WiredLoad } from '../model';
import { isWired } from '../model';

/**
 * Stable key for a physical output. Two loads sharing a key are wired to the
 * same output, which is the definition of a duplicate-channel conflict.
 */
export const channelKey = (ref: ChannelRef): string =>
  ref.kind === CHANNEL_KIND.RCU
    ? `${CHANNEL_KIND.RCU}:${ref.channel}`
    : `${CHANNEL_KIND.DIN}:${ref.railId}:${ref.channel}`;

export const findLoad = (config: RcuConfig, id: LoadId): Load | undefined =>
  config.loads.find((load) => load.id === id);

export const findRail = (config: RcuConfig, id: RailId): DinRail | undefined =>
  config.rails.find((rail) => rail.id === id);

export const findRoom = (config: RcuConfig, id: RoomId): Room | undefined =>
  config.rooms.find((room) => room.id === id);

export const findDaliBridge = (config: RcuConfig, id: DaliBridgeId): DaliBridge | undefined =>
  config.daliBridges.find((bridge) => bridge.id === id);

/** Bridges in address order, which is how a technician walks the cabinet. */
export const sortedDaliBridges = (config: RcuConfig): readonly DaliBridge[] =>
  [...config.daliBridges].sort((left, right) => left.address - right.address);

export const takenBridgeAddresses = (config: RcuConfig): ReadonlySet<number> =>
  new Set(config.daliBridges.map((bridge) => bridge.address));

export const takenBridgeMasters = (config: RcuConfig): ReadonlySet<number> =>
  new Set(config.daliBridges.map((bridge) => bridge.master));

const BRIDGE_ADDRESS_RANGE: readonly number[] = Array.from(
  { length: DALI_BRIDGE_ADDRESS.MAX - DALI_BRIDGE_ADDRESS.MIN + 1 },
  (_unused, index) => DALI_BRIDGE_ADDRESS.MIN + index,
);

export const DALI_BRIDGE_ADDRESS_RANGE = BRIDGE_ADDRESS_RANGE;

/** Lowest unused bridge address, or null when every address is taken. */
export const nextFreeBridgeAddress = (config: RcuConfig): number | null => {
  const taken = takenBridgeAddresses(config);
  return BRIDGE_ADDRESS_RANGE.find((address) => !taken.has(address)) ?? null;
};

/** Lowest unused DALI bus, or null when every one is taken. */
export const nextFreeBridgeMaster = (config: RcuConfig): number | null => {
  const taken = takenBridgeMasters(config);
  return BRIDGE_ADDRESS_RANGE.find((master) => !taken.has(master)) ?? null;
};

export const railsOfType = (config: RcuConfig, type: RailTypeKey): readonly DinRail[] =>
  config.rails.filter((rail) => rail.type === type);

export const takenRailAddresses = (config: RcuConfig, type: RailTypeKey): ReadonlySet<number> =>
  new Set(railsOfType(config, type).map((rail) => rail.address));

export const isRailAddressTaken = (
  config: RcuConfig,
  type: RailTypeKey,
  address: number,
): boolean => takenRailAddresses(config, type).has(address);

/** Lowest unused address for a rail type, or null when every address is taken. */
export const nextFreeRailAddress = (config: RcuConfig, type: RailTypeKey): number | null => {
  const taken = takenRailAddresses(config, type);
  return RAIL_ADDRESS_RANGE.find((address) => !taken.has(address)) ?? null;
};

/** Outputs available at a target: the RCU's configured count, or 0 if the rail is gone. */
export const channelCountAt = (config: RcuConfig, ref: ChannelRef): number => {
  if (ref.kind === CHANNEL_KIND.RCU) return config.rcu.outputCount;
  const rail = findRail(config, ref.railId);
  return rail === undefined ? 0 : RAIL_TYPE_SPECS[rail.type].channels;
};

export const loadsOnRail = (config: RcuConfig, railId: RailId): readonly Load[] =>
  config.loads.filter(
    (load) =>
      load.channel !== null &&
      load.channel.kind === CHANNEL_KIND.DIN &&
      load.channel.railId === railId,
  );

/** The load wired to a channel, if any. Narrowed, because being there means it is wired. */
export const loadAtChannel = (config: RcuConfig, ref: ChannelRef): WiredLoad | undefined => {
  const key = channelKey(ref);
  return config.loads.find(
    (load): load is WiredLoad => isWired(load) && channelKey(load.channel) === key,
  );
};
